/**
 * Campaigns tab — list and manage loyalty campaigns.
 *
 * Status filter tabs, card-based campaign list, create/edit/activate/end actions.
 * Follows MonthlyCouponsTab layout pattern.
 *
 * Feature 9 — OPS-1862 (Campaign Management System)
 */

import { useState } from "react";
import {
  Loader2,
  Plus,
  Megaphone,
  Users,
  Cake,
  Crown,
  TrendingDown,
  Coins,
  Ticket,
  Zap,
  Play,
  Square,
  Calendar,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  useCampaignList,
  useActivateCampaign,
  useEndCampaign,
  type CampaignItem,
} from "../api/rewardsAdminHooks";
import { CampaignForm } from "./CampaignForm";

const STATUS_TABS = [
  { value: undefined, label: "ทั้งหมด" },
  { value: "Active", label: "กำลังทำงาน" },
  { value: "Scheduled", label: "กำหนดไว้" },
  { value: "Draft", label: "ฉบับร่าง" },
  { value: "Ended", label: "สิ้นสุดแล้ว" },
] as const;

const statusBadgeVariant: Record<string, string> = {
  Draft: "bg-gray-100 text-gray-600",
  Scheduled: "bg-blue-100 text-blue-700",
  Active: "bg-green-100 text-green-700",
  Ended: "bg-gray-100 text-gray-500",
};

const audienceIcons: Record<string, typeof Users> = {
  "All Members": Users,
  "Birthday This Month": Cake,
  "Specific Tier": Crown,
  "Low Retention": TrendingDown,
};

const rewardIcons: Record<string, typeof Coins> = {
  "Bonus Points": Coins,
  Coupon: Ticket,
  "Point Multiplier": Zap,
};

function formatDate(d: string | null): string {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("th-TH", {
    day: "numeric",
    month: "short",
    year: "2-digit",
  });
}

export function CampaignsTab() {
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [showForm, setShowForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<string | null>(null);

  const listQuery = useCampaignList(statusFilter);
  const activateMutation = useActivateCampaign();
  const endMutation = useEndCampaign();

  const handleActivate = (name: string) => {
    activateMutation.mutate(name, {
      onSuccess: (data) => {
        toast.success(
          `เปิดใช้งานแคมเปญสำเร็จ — ส่งรางวัลให้ ${data.members_targeted} สมาชิก`,
        );
      },
      onError: (err) => toast.error(err.message || "เกิดข้อผิดพลาด"),
    });
  };

  const handleEnd = (name: string) => {
    endMutation.mutate(name, {
      onSuccess: () => toast.success("สิ้นสุดแคมเปญสำเร็จ"),
      onError: (err) => toast.error(err.message || "เกิดข้อผิดพลาด"),
    });
  };

  if (showForm || editingCampaign) {
    return (
      <CampaignForm
        campaignName={editingCampaign}
        onClose={() => {
          setShowForm(false);
          setEditingCampaign(null);
        }}
      />
    );
  }

  if (listQuery.isLoading) {
    return (
      <div className="space-y-3 pt-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-lg" />
        ))}
      </div>
    );
  }

  const campaigns = listQuery.data?.campaigns ?? [];

  return (
    <div className="space-y-6 pt-4">
      {/* Header with create button */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {STATUS_TABS.map((tab) => (
            <Button
              key={tab.label}
              variant={statusFilter === tab.value ? "default" : "ghost"}
              size="sm"
              onClick={() => setStatusFilter(tab.value)}
              className="text-xs"
            >
              {tab.label}
            </Button>
          ))}
        </div>
        <Button size="sm" onClick={() => setShowForm(true)}>
          <Plus className="mr-1 h-3 w-3" />
          สร้างแคมเปญ
        </Button>
      </div>

      {/* Campaign list */}
      {campaigns.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-12 text-center">
          <Megaphone className="mb-3 h-10 w-10 text-muted-foreground" />
          <p className="text-sm font-medium text-muted-foreground">
            ยังไม่มีแคมเปญ
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            สร้างแคมเปญเพื่อส่งรางวัลให้กลุ่มเป้าหมาย
          </p>
        </div>
      )}

      <div className="space-y-3">
        {campaigns.map((c) => (
          <CampaignCard
            key={c.name}
            campaign={c}
            onEdit={() => setEditingCampaign(c.name)}
            onActivate={() => handleActivate(c.name)}
            onEnd={() => handleEnd(c.name)}
            isActivating={activateMutation.isPending}
            isEnding={endMutation.isPending}
          />
        ))}
      </div>
    </div>
  );
}

function CampaignCard({
  campaign: c,
  onEdit,
  onActivate,
  onEnd,
  isActivating,
  isEnding,
}: {
  campaign: CampaignItem;
  onEdit: () => void;
  onActivate: () => void;
  onEnd: () => void;
  isActivating: boolean;
  isEnding: boolean;
}) {
  const AudienceIcon = audienceIcons[c.audience_type] ?? Users;
  const RewardIcon = rewardIcons[c.reward_type] ?? Coins;

  return (
    <div className="rounded-lg border p-4 space-y-3 hover:shadow-apple-sm transition-shadow">
      {/* Top row: name + status */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <h3 className="text-sm font-semibold">{c.campaign_name}</h3>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <AudienceIcon className="h-3 w-3" />
              {c.audience_type}
            </span>
            <span className="flex items-center gap-1">
              <RewardIcon className="h-3 w-3" />
              {c.reward_type === "Bonus Points"
                ? `${c.reward_points} แต้ม`
                : c.reward_type === "Point Multiplier"
                  ? `x${c.reward_multiplier} คูณแต้ม`
                  : c.reward_coupon_template || "คูปอง"}
            </span>
          </div>
        </div>
        <Badge
          variant="secondary"
          className={statusBadgeVariant[c.status] ?? ""}
        >
          {c.status}
        </Badge>
      </div>

      {/* Bottom row: dates + actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {formatDate(c.start_date)} — {formatDate(c.end_date)}
          </span>
          {c.members_targeted > 0 && (
            <span className="flex items-center gap-1">
              <Users className="h-3 w-3" />
              {c.members_targeted} สมาชิก
            </span>
          )}
        </div>

        <div className="flex gap-1">
          {(c.status === "Draft" || c.status === "Scheduled") && (
            <>
              <Button variant="ghost" size="sm" onClick={onEdit}>
                แก้ไข
              </Button>
              <Button
                size="sm"
                onClick={onActivate}
                disabled={isActivating}
              >
                {isActivating ? (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                ) : (
                  <Play className="mr-1 h-3 w-3" />
                )}
                เปิดใช้งาน
              </Button>
            </>
          )}
          {c.status === "Active" && (
            <Button
              variant="outline"
              size="sm"
              onClick={onEnd}
              disabled={isEnding}
            >
              {isEnding ? (
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              ) : (
                <Square className="mr-1 h-3 w-3" />
              )}
              สิ้นสุด
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
